import isEqual from "lodash/isEqual";
import React, { createContext, useCallback, useContext, useState } from "react";
import { FormWidgetProps } from ".";

// Component
import ImageManager from "../../witadmin/CRUD/Common/ImageManager";
import { FormFieldConfiguration } from "../crud/form";
import { SectionLabel } from "./SectionLabel";
import { ErrorField } from "./TranslationComponent/styles";

// Constants
const BUCKET_OLD = "witbooking.content.user";
const BUCKET_NEW = "witbooking-user-content";
const SIZE_PREVIEW_OLD = "125x77";
const SIZE_PREVIEW_NEW = "100";

// Interfaces
interface Errors {
  overSizeLimit?: Array<String>;
  containsSpecial?: Array<String>;
  belowMinWidth?: Array<String>;
}

interface ImageManagerConfig {
  locales: string[];
  maxSizeMB?: number;
  maxFiles?: number;
  minWidth?: number;
  allowEdit?: boolean;
  allowDeleteSelection?: boolean;
  formats?: string;
  addBackgroundToPreview?: boolean;
  helpText?: {
    id: string;
    defaultMessage: string;
    values?: Record<string, number>;
  };
  error?: React.ReactNode | string;
  hideLimitMessage?: boolean;
}

type ImageManagerProps<T> = FormWidgetProps<T> & {
  config?: ImageManagerConfig;
};

export interface Translation {
  [locale: string]: {
    title: string;
    description?: string;
  };
}

interface ImageManagerValue extends File {
  id: string;
  translations?: Translation;
  modifiedName: string;
  preview: string;
  url: string;
  labels?: string[];
  caption?: string;
}

export interface Image extends File {
  id: string;
  filename: string;
  url: string;
  translations?: Translation;
  labels?: string[];
  caption?: string;
}

interface EventPayload {
  value: ImageManagerValue[];
  errors?: Errors;
}

interface DataByActions {
  create: Image[];
  update: Image[];
  remove: Image[];
}

// Helpers
function getDataDelete(before: Image[], now: Image[]): Image[] {
  if (!now.length) {
    return before;
  }
  return before.filter(b => !now.find(n => n.id === b.id));
}

function getDataCreated(before: Image[], now: Image[]): Image[] {
  if (!before.length) {
    return now;
  }
  return now.filter(n => before.find(b => b.id !== n.id) && n.size);
}

function getDataModified(before: Image[], now: Image[]): Image[] {
  return now.filter(n =>
    before.find(b => n.id === b.id && !isEqual(n.translations, b.translations))
  );
}

export function getDataByActions(before: Image[], now: Image[]): DataByActions {
  return {
    create: getDataCreated(before, now),
    update: getDataModified(before, now),
    remove: getDataDelete(before, now),
  };
}

function getImageAndExtension(filenameWithExtension: string): string[] {
  return filenameWithExtension.split(".");
}

function getImagePreview(url: string): string {
  if (!url) {
    return "";
  }
  const [protocol, , server, step1, step2, step3, step4, step5] = url.split("/");
  if (step1.includes(BUCKET_OLD) && !step5.includes(SIZE_PREVIEW_OLD)) {
    const [filename, extension] = getImageAndExtension(step5);

    return `${protocol}//${server}/${step1}/${step2}/${step3}/${step4}/${filename}_${SIZE_PREVIEW_OLD}.${extension}`;
  } else if (server.includes(BUCKET_NEW) && step1 !== "generated") {
    const [filename] = getImageAndExtension(step3);

    return `${protocol}//${server}/generated/${step1}/${step2}/${filename}-${SIZE_PREVIEW_NEW}.webp`;
  }

  return url;
}

function imageFromImageManagerValue(images: Image[] = []): ImageManagerValue[] {
  return images.map(image =>
    Object.assign(new File([image.slice ? image.slice() : ""], image.filename), {
      preview: getImagePreview(image.url),
      url: image.url,
      id: image.id,
      modifiedName: image.filename,
      translations: image.translations || {},
      labels: image.labels,
      caption: image.caption,
    } as ImageManagerValue)
  );
}

function imageManagerValueFromImage(files: ImageManagerValue[]): Image[] {
  return files.map((imageManagerValue: ImageManagerValue) => {
    const image: Image = Object.assign(
      new File([imageManagerValue.slice()], imageManagerValue.name),
      {
        id: imageManagerValue.id,
        filename: imageManagerValue.name,
        url: imageManagerValue.url || imageManagerValue.preview,
        translations: imageManagerValue.translations,
        labels: imageManagerValue.labels,
        caption: imageManagerValue.caption,
      } as Image
    );
    return image;
  });
}

// Contexts
const ErrorContext = createContext<{
  errors: Errors;
  setErrors: (e: Errors) => void;
}>({
  errors: {},
  setErrors: (e: Errors) => {},
});

// Main
function ImageManagerWidget<T extends Image[]>({
  schema,
  idSchema,
  formData,
  config,
  onChange,
  isDisabled = false,
  error,
}: ImageManagerProps<T>) {
  const { errors, setErrors } = useContext(ErrorContext);
  const handleOnChange = useCallback(
    (payload: EventPayload) => {
      setErrors(payload.errors || {});
      onChange(imageManagerValueFromImage(payload.value));
    },
    [onChange, setErrors]
  );
  return (
    <div id={idSchema.$id}>
      <SectionLabel>{schema.title}</SectionLabel>
      {error && <ErrorField>{error}</ErrorField>}
      <ImageManager
        isDisabled={isDisabled}
        allowImageEdit={config?.allowEdit ?? true}
        allowDeleteSelection={!isDisabled && (config?.allowDeleteSelection ?? true)}
        config={{
          maxSizeMB: config?.maxSizeMB,
          maxFiles: config?.maxFiles,
          //@ts-ignore
          minWidth: config?.minWidth,
          formats: config?.formats,
          addBackgroundToPreview: config?.addBackgroundToPreview,
          helpText: config?.helpText,
          hideLimitMessage: config?.hideLimitMessage,
        }}
        field={idSchema.$id}
        locales={config?.locales}
        formValues={{ multimedia: imageFromImageManagerValue(formData) }}
        onChange={handleOnChange}
        cleanFiles={false}
        errors={errors}
      />
    </div>
  );
}

export function ImageManagerProvider({ children }: any) {
  const [errors, setErrors] = useState<Errors>({});
  return <ErrorContext.Provider value={{ errors, setErrors }}>{children}</ErrorContext.Provider>;
}

export function imageManagerConfig<T extends Image[]>(
  config: ImageManagerConfig
): FormFieldConfiguration<T> {
  return {
    fieldWidget: (props: FormWidgetProps<T>) => <ImageManagerWidget {...{ ...props, config }} />,
  };
}

export const renderImageComponent = ({ config, ...rest }: any) => (
  <ImageManagerWidget {...{ config, ...rest }} />
);
